import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';

import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { 
  Users, 
  Calendar, 
  ClipboardList, 
  BarChart3,
  Target,
  Clock,
  CheckCircle,
  AlertCircle,
  Star,
  TrendingUp,
  FileText,
  Settings,
  Plus,
  Edit,
  Eye,
  Download
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const PerformanceEvaluationPage = () => {
  const { user } = useAuth();
  const [currentTab, setCurrentTab] = useState('dashboard');
  const [loading, setLoading] = useState(false);
  const allowedTabs = ['dashboard', 'cycles', 'evaluate', 'reports', 'settings'];
  
  // Data states
  const [cycles, setCycles] = useState([]);
  const [competencies, setCompetencies] = useState([]);
  const [pendingEvaluations, setPendingEvaluations] = useState([]);
  const [employees, setEmployees] = useState([]);

  // Form states
  const [newCycle, setNewCycle] = useState({
    name: '',
    description: '',
    start_date: '',
    end_date: '',
    evaluation_deadline: '',
    approval_deadline: '',
    competencies: [],
    evaluator_types: ['superior', 'self']
  });

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadCycles(),
        loadCompetencies(),
        loadPendingEvaluations(),
        loadEmployees()
      ]);
    } catch (error) {
      console.error('Error loading initial data:', error);
      toast.error('Erro ao carregar dados iniciais');
    } finally {
      setLoading(false);
    }
  };

  const loadCycles = async () => {
    try {
      const response = await axios.get(`${API}/api/performance/cycles`);
      setCycles(response.data);
    } catch (error) {
      console.error('Error loading cycles:', error);
    }
  };

  const loadCompetencies = async () => {
    try {
      const response = await axios.get(`${API}/api/performance/competencies`);
      setCompetencies(response.data);
    } catch (error) {
      console.error('Error loading competencies:', error);
    }
  };

  const loadPendingEvaluations = async () => {
    try {
      const response = await axios.get(`${API}/api/performance/evaluations/my-pending`);
      setPendingEvaluations(response.data);
    } catch (error) {
      console.error('Error loading pending evaluations:', error);
    }
  };

  const loadEmployees = async () => {
    try {
      const response = await axios.get(`${API}/api/employees`);
      setEmployees(response.data);
    } catch (error) {
      console.error('Error loading employees:', error);
    }
  };

  const handleCreateCycle = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      await axios.post(`${API}/api/performance/cycles`, newCycle);
      toast.success('Ciclo de avaliação criado com sucesso!');
      setNewCycle({
        name: '',
        description: '',
        start_date: '',
        end_date: '',
        evaluation_deadline: '',
        approval_deadline: '',
        competencies: [],
        evaluator_types: ['superior', 'self']
      });
      await loadCycles();
    } catch (error) {
      console.error('Error creating cycle:', error);
      toast.error('Erro ao criar ciclo de avaliação');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateCycleStatus = async (cycleId, status) => {
    try {
      await axios.put(`${API}/api/performance/cycles/${cycleId}/status?status=${status}`);
      toast.success(`Ciclo ${status === 'active' ? 'ativado' : 'finalizado'} com sucesso!`);
      await loadCycles();
    } catch (error) {
      console.error('Error updating cycle status:', error);
      toast.error('Erro ao atualizar status do ciclo');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const getCycleStatusBadge = (status) => {
    const statusConfig = {
      draft: { label: 'Rascunho', color: 'gray' },
      active: { label: 'Ativo', color: 'green' },
      completed: { label: 'Finalizado', color: 'blue' },
      cancelled: { label: 'Cancelado', color: 'red' }
    };
    
    const config = statusConfig[status] || statusConfig.draft;
    return <Badge variant={config.color}>{config.label}</Badge>;
  };

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Avaliação de Desempenho</h1>
          <p className="text-gray-600 mt-2">
            Gerencie ciclos de avaliação e acompanhe o desenvolvimento dos colaboradores
          </p>
        </div>
        <div className="flex space-x-2">
          <Button variant="outline" onClick={loadInitialData}>
            <BarChart3 className="h-4 w-4 mr-2" />
            Atualizar
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={currentTab} onValueChange={setCurrentTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-5">
          {allowedTabs.includes('dashboard') && <TabsTrigger value="dashboard">Dashboard</TabsTrigger>}
          {allowedTabs.includes('cycles') && <TabsTrigger value="cycles">Ciclos</TabsTrigger>}
          {allowedTabs.includes('evaluate') && <TabsTrigger value="evaluate">Avaliar</TabsTrigger>}
          {allowedTabs.includes('reports') && <TabsTrigger value="reports">Relatórios</TabsTrigger>}
          {allowedTabs.includes('settings') && <TabsTrigger value="settings">Configurações</TabsTrigger>}
        </TabsList>

        {/* Dashboard Tab */}
        {allowedTabs.includes('dashboard') && (
        <TabsContent value="dashboard">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
            {/* Stats Cards */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Ciclos Ativos</CardTitle>
                <Calendar className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {cycles.filter(c => c.status === 'active').length}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Avaliações Pendentes</CardTitle>
                <Clock className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-orange-600">
                  {pendingEvaluations.length}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Funcionários</CardTitle>
                <Users className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {employees.length}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Competências</CardTitle>
                <Target className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {competencies.length}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Pending Evaluations */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <AlertCircle className="h-5 w-5 text-orange-500" />
                <span>Avaliações Pendentes</span>
              </CardTitle>
              <CardDescription>
                Avaliações que requerem sua atenção
              </CardDescription>
            </CardHeader>
            <CardContent>
              {pendingEvaluations.length === 0 ? (
                <div className="text-center py-8">
                  <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
                  <p className="text-gray-500">Nenhuma avaliação pendente!</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {pendingEvaluations.slice(0, 5).map((pending, index) => (
                    <div key={index} className="flex items-center justify-between p-4 border rounded-lg">
                      <div>
                        <h4 className="font-medium">{pending.employee.nome}</h4>
                        <p className="text-sm text-gray-600">
                          Ciclo: {pending.cycle.name}
                        </p>
                        <p className="text-xs text-gray-500">
                          Prazo: {formatDate(pending.cycle.evaluation_deadline)}
                        </p>
                      </div>
                      <Button 
                        size="sm"
                        onClick={() => setCurrentTab('evaluate')}
                      >
                        <Edit className="h-4 w-4 mr-2" />
                        Avaliar
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        )}

        {/* Cycles Tab */}
        {allowedTabs.includes('cycles') && (
        <TabsContent value="cycles">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Create New Cycle */}
            <Card className="lg:col-span-1">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Plus className="h-5 w-5" />
                  <span>Novo Ciclo</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleCreateCycle} className="space-y-4">
                  <div>
                    <Label htmlFor="cycle-name">Nome do Ciclo *</Label>
                    <Input
                      id="cycle-name"
                      value={newCycle.name}
                      onChange={(e) => setNewCycle(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Ex: Avaliação 2024.1"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="cycle-description">Descrição</Label>
                    <Textarea
                      id="cycle-description"
                      value={newCycle.description}
                      onChange={(e) => setNewCycle(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Descrição do ciclo de avaliação"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label htmlFor="start-date">Data Início *</Label>
                      <Input
                        id="start-date"
                        type="date"
                        value={newCycle.start_date}
                        onChange={(e) => setNewCycle(prev => ({ ...prev, start_date: e.target.value }))}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="end-date">Data Fim *</Label>
                      <Input
                        id="end-date"
                        type="date"
                        value={newCycle.end_date}
                        onChange={(e) => setNewCycle(prev => ({ ...prev, end_date: e.target.value }))}
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label htmlFor="eval-deadline">Prazo Avaliação *</Label>
                      <Input
                        id="eval-deadline"
                        type="date"
                        value={newCycle.evaluation_deadline}
                        onChange={(e) => setNewCycle(prev => ({ ...prev, evaluation_deadline: e.target.value }))}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="approval-deadline">Prazo Aprovação *</Label>
                      <Input
                        id="approval-deadline"
                        type="date"
                        value={newCycle.approval_deadline}
                        onChange={(e) => setNewCycle(prev => ({ ...prev, approval_deadline: e.target.value }))}
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <Label>Competências Avaliadas</Label>
                    <div className="mt-2 space-y-2 max-h-32 overflow-y-auto border rounded p-2">
                      {competencies.map(comp => (
                        <label key={comp.id} className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={newCycle.competencies.includes(comp.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setNewCycle(prev => ({
                                  ...prev,
                                  competencies: [...prev.competencies, comp.id]
                                }));
                              } else {
                                setNewCycle(prev => ({
                                  ...prev,
                                  competencies: prev.competencies.filter(id => id !== comp.id)
                                }));
                              }
                            }}
                          />
                          <span className="text-sm">{comp.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <Button type="submit" className="w-full" disabled={loading}>
                    {loading ? 'Criando...' : 'Criar Ciclo'}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Existing Cycles */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle>Ciclos de Avaliação</CardTitle>
                <CardDescription>
                  Gerencie os ciclos existentes e seus status
                </CardDescription>
              </CardHeader>
              <CardContent>
                {cycles.length === 0 ? (
                  <div className="text-center py-8">
                    <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">Nenhum ciclo criado ainda</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {cycles.map(cycle => (
                      <Card key={cycle.id} className="border-l-4 border-l-blue-500">
                        <CardContent className="pt-4">
                          <div className="flex justify-between items-start">
                            <div className="space-y-2">
                              <div className="flex items-center space-x-2">
                                <h4 className="font-semibold">{cycle.name}</h4>
                                {getCycleStatusBadge(cycle.status)}
                              </div>
                              
                              {cycle.description && (
                                <p className="text-sm text-gray-600">{cycle.description}</p>
                              )}
                              
                              <div className="flex items-center space-x-4 text-sm text-gray-500">
                                <span>Início: {formatDate(cycle.start_date)}</span>
                                <span>Fim: {formatDate(cycle.end_date)}</span>
                                <span>Competências: {cycle.competencies?.length || 0}</span>
                              </div>
                            </div>
                            
                            <div className="flex space-x-2">
                              {cycle.status === 'draft' && (
                                <Button
                                  size="sm"
                                  onClick={() => handleUpdateCycleStatus(cycle.id, 'active')}
                                >
                                  Ativar
                                </Button>
                              )}
                              {cycle.status === 'active' && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleUpdateCycleStatus(cycle.id, 'completed')}
                                >
                                  Finalizar
                                </Button>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
        )}

        {/* Evaluate Tab */}
        {allowedTabs.includes('evaluate') && (
        <TabsContent value="evaluate">
          <EvaluationInterface
            pendingEvaluations={pendingEvaluations}
            competencies={competencies}
            onEvaluationComplete={loadPendingEvaluations}
          />
        </TabsContent>
        )}

        {/* Reports Tab */}
        {allowedTabs.includes('reports') && (
        <TabsContent value="reports">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <BarChart3 className="h-5 w-5" />
                <span>Relatórios de Avaliação</span>
              </CardTitle>
              <CardDescription>
                Visualize resultados e análises das avaliações
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-center py-8">
                <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">Relatórios em desenvolvimento</p>
                <p className="text-sm text-gray-400 mt-2">
                  Em breve você poderá visualizar relatórios detalhados das avaliações
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        )}

        {/* Settings Tab */}
        {allowedTabs.includes('settings') && (
        <TabsContent value="settings">
          <CompetencyManager
            competencies={competencies}
            onCompetenciesChange={loadCompetencies}
          />
        </TabsContent>
        )}
      </Tabs>
    </div>
  );
};

// Evaluation Interface Component
const EvaluationInterface = ({ pendingEvaluations, competencies, onEvaluationComplete }) => {
  const [selectedEvaluation, setSelectedEvaluation] = useState(null);
  const [responses, setResponses] = useState({});
  const [overallComments, setOverallComments] = useState('');
  const [developmentPriorities, setDevelopmentPriorities] = useState([]);
  const [loading, setLoading] = useState(false);

  const barsScale = [
    { level: 1, label: "Muito Abaixo do Esperado", description: "Performance significativamente abaixo das expectativas" },
    { level: 2, label: "Abaixo do Esperado", description: "Performance parcialmente abaixo das expectativas" },
    { level: 3, label: "Atende às Expectativas", description: "Performance consistente com o esperado" },
    { level: 4, label: "Acima do Esperado", description: "Performance superior às expectativas" },
    { level: 5, label: "Muito Acima do Esperado", description: "Performance excepcional, referência para outros" }
  ];

  const handleSubmitEvaluation = async () => {
    if (!selectedEvaluation) return;

    try {
      setLoading(true);
      
      const evaluationData = {
        cycle_id: selectedEvaluation.cycle.id,
        employee_id: selectedEvaluation.employee.id,
        evaluator_type: selectedEvaluation.evaluator_type,
        responses: Object.entries(responses).map(([competency_id, data]) => ({
          competency_id,
          rating: data.rating,
          comments: data.comments || '',
          evidence: data.evidence || ''
        })),
        overall_comments: overallComments,
        development_priorities: developmentPriorities
      };

      await axios.post(`${API}/api/performance/evaluations`, evaluationData);
      toast.success('Avaliação salva com sucesso!');
      
      // Reset form
      setSelectedEvaluation(null);
      setResponses({});
      setOverallComments('');
      setDevelopmentPriorities([]);
      
      // Reload pending evaluations
      onEvaluationComplete();
      
    } catch (error) {
      console.error('Error submitting evaluation:', error);
      toast.error('Erro ao salvar avaliação');
    } finally {
      setLoading(false);
    }
  };

  const updateResponse = (competencyId, field, value) => {
    setResponses(prev => ({
      ...prev,
      [competencyId]: {
        ...prev[competencyId],
        [field]: value
      }
    }));
  };

  if (!selectedEvaluation) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <ClipboardList className="h-5 w-5" />
            <span>Realizar Avaliação</span>
          </CardTitle>
          <CardDescription>
            Selecione uma avaliação pendente para começar
          </CardDescription>
        </CardHeader>
        <CardContent>
          {pendingEvaluations.length === 0 ? (
            <div className="text-center py-8">
              <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
              <p className="text-gray-500">Nenhuma avaliação pendente!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {pendingEvaluations.map((pending, index) => (
                <Card 
                  key={index} 
                  className="cursor-pointer hover:border-blue-500 transition-colors"
                  onClick={() => setSelectedEvaluation(pending)}
                >
                  <CardContent className="pt-4">
                    <div className="space-y-2">
                      <h4 className="font-semibold">{pending.employee.nome}</h4>
                      <p className="text-sm text-gray-600">
                        <strong>Ciclo:</strong> {pending.cycle.name}
                      </p>
                      <p className="text-sm text-gray-600">
                        <strong>Cargo:</strong> {pending.employee.cargo || 'Não informado'}
                      </p>
                      <p className="text-xs text-gray-500">
                        Prazo: {new Date(pending.cycle.evaluation_deadline).toLocaleDateString('pt-BR')}
                      </p>
                      <Button size="sm" className="w-full mt-2">
                        <Edit className="h-4 w-4 mr-2" />
                        Iniciar Avaliação
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  const cycleCompetencies = competencies.filter(comp => 
    selectedEvaluation.cycle.competencies?.includes(comp.id)
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex justify-between items-start">
            <div>
              <h3 className="text-lg font-semibold">
                Avaliando: {selectedEvaluation.employee.nome}
              </h3>
              <p className="text-gray-600">
                Ciclo: {selectedEvaluation.cycle.name}
              </p>
              <p className="text-sm text-gray-500">
                Cargo: {selectedEvaluation.employee.cargo || 'Não informado'}
              </p>
            </div>
            <Button
              variant="outline"
              onClick={() => setSelectedEvaluation(null)}
            >
              ← Voltar
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Competency Evaluations */}
      <Card>
        <CardHeader>
          <CardTitle>Avaliação por Competências</CardTitle>
          <CardDescription>
            Avalie cada competência usando a escala BARS (1-5)
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {cycleCompetencies.map(competency => (
            <div key={competency.id} className="border rounded-lg p-4 space-y-4">
              <div>
                <h4 className="font-semibold text-lg">{competency.name}</h4>
                <p className="text-gray-600 text-sm">{competency.description}</p>
                <Badge variant="outline">{competency.category}</Badge>
              </div>

              {/* BARS Scale Selection */}
              <div className="space-y-2">
                <Label>Avaliação *</Label>
                <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
                  {barsScale.map(scale => (
                    <div
                      key={scale.level}
                      className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                        responses[competency.id]?.rating === scale.level
                          ? 'border-blue-500 bg-blue-50'
                          : 'hover:border-gray-400'
                      }`}
                      onClick={() => updateResponse(competency.id, 'rating', scale.level)}
                    >
                      <div className="text-center">
                        <div className="font-semibold">{scale.level}</div>
                        <div className="text-xs mt-1">{scale.label}</div>
                      </div>
                    </div>
                  ))}
                </div>
                {responses[competency.id]?.rating && (
                  <div className="mt-2 p-2 bg-blue-50 rounded text-sm">
                    <strong>{barsScale.find(s => s.level === responses[competency.id].rating)?.label}</strong>
                    <p className="text-gray-600 mt-1">
                      {barsScale.find(s => s.level === responses[competency.id].rating)?.description}
                    </p>
                  </div>
                )}
              </div>

              {/* Comments */}
              <div>
                <Label htmlFor={`comments-${competency.id}`}>
                  Comentários e Observações
                </Label>
                <Textarea
                  id={`comments-${competency.id}`}
                  value={responses[competency.id]?.comments || ''}
                  onChange={(e) => updateResponse(competency.id, 'comments', e.target.value)}
                  placeholder="Descreva comportamentos observados, exemplos específicos..."
                  rows={3}
                />
              </div>

              {/* Evidence */}
              <div>
                <Label htmlFor={`evidence-${competency.id}`}>
                  Evidências / Exemplos Específicos
                </Label>
                <Textarea
                  id={`evidence-${competency.id}`}
                  value={responses[competency.id]?.evidence || ''}
                  onChange={(e) => updateResponse(competency.id, 'evidence', e.target.value)}
                  placeholder="Cite projetos, situações, resultados específicos..."
                  rows={2}
                />
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Overall Assessment */}
      <Card>
        <CardHeader>
          <CardTitle>Avaliação Geral</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="overall-comments">Comentários Gerais da Avaliação</Label>
            <Textarea
              id="overall-comments"
              value={overallComments}
              onChange={(e) => setOverallComments(e.target.value)}
              placeholder="Síntese geral do desempenho, principais conquistas, desafios..."
              rows={4}
            />
          </div>

          <div>
            <Label htmlFor="development-priorities">Prioridades de Desenvolvimento</Label>
            <Textarea
              id="development-priorities"
              value={developmentPriorities.join('\n')}
              onChange={(e) => setDevelopmentPriorities(e.target.value.split('\n').filter(p => p.trim()))}
              placeholder="Uma prioridade por linha..."
              rows={3}
            />
          </div>
        </CardContent>
      </Card>

      {/* Submit */}
      <div className="flex justify-end space-x-2">
        <Button 
          variant="outline" 
          onClick={() => setSelectedEvaluation(null)}
        >
          Cancelar
        </Button>
        <Button 
          onClick={handleSubmitEvaluation}
          disabled={loading || Object.keys(responses).length !== cycleCompetencies.length}
        >
          {loading ? 'Salvando...' : 'Salvar Avaliação'}
        </Button>
      </div>
    </div>
  );
};

// Competency Manager Component
const CompetencyManager = ({ competencies, onCompetenciesChange }) => {
  const [newCompetency, setNewCompetency] = useState({
    name: '',
    description: '',
    category: 'behavioral'
  });
  const [loading, setLoading] = useState(false);

  const handleCreateCompetency = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      await axios.post(`${API}/api/performance/competencies`, newCompetency);
      toast.success('Competência criada com sucesso!');
      setNewCompetency({ name: '', description: '', category: 'behavioral' });
      onCompetenciesChange();
    } catch (error) {
      console.error('Error creating competency:', error);
      toast.error('Erro ao criar competência');
    } finally {
      setLoading(false);
    }
  };

  const competenciesByCategory = competencies.reduce((acc, comp) => {
    if (!acc[comp.category]) acc[comp.category] = [];
    acc[comp.category].push(comp);
    return acc;
  }, {});

  const categoryLabels = {
    behavioral: 'Comportamentais',
    technical: 'Técnicas',
    leadership: 'Liderança'
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Create New Competency */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Plus className="h-5 w-5" />
            <span>Nova Competência</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleCreateCompetency} className="space-y-4">
            <div>
              <Label htmlFor="comp-name">Nome *</Label>
              <Input
                id="comp-name"
                value={newCompetency.name}
                onChange={(e) => setNewCompetency(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Ex: Comunicação"
                required
              />
            </div>

            <div>
              <Label htmlFor="comp-description">Descrição *</Label>
              <Textarea
                id="comp-description"
                value={newCompetency.description}
                onChange={(e) => setNewCompetency(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Descreva a competência..."
                required
              />
            </div>

            <div>
              <Label htmlFor="comp-category">Categoria *</Label>
              <Select
                value={newCompetency.category}
                onValueChange={(value) => setNewCompetency(prev => ({ ...prev, category: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="behavioral">Comportamental</SelectItem>
                  <SelectItem value="technical">Técnica</SelectItem>
                  <SelectItem value="leadership">Liderança</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? 'Criando...' : 'Criar Competência'}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Existing Competencies */}
      <Card className="lg:col-span-2">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Settings className="h-5 w-5" />
            <span>Competências Cadastradas</span>
          </CardTitle>
          <CardDescription>
            {competencies.length} competências disponíveis
          </CardDescription>
        </CardHeader>
        <CardContent>
          {Object.entries(competenciesByCategory).map(([category, comps]) => (
            <div key={category} className="mb-6">
              <h4 className="font-semibold mb-3 text-gray-900">
                {categoryLabels[category]} ({comps.length})
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {comps.map(comp => (
                  <div key={comp.id} className="border rounded-lg p-3">
                    <h5 className="font-medium">{comp.name}</h5>
                    <p className="text-sm text-gray-600 mt-1">{comp.description}</p>
                    <Badge variant="outline" className="mt-2">{comp.category}</Badge>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
};

export default PerformanceEvaluationPage;